import {
  approveIdentityGroupJoin,
  cancelIdentityGroupJoin,
  getPublicAccountsByIds,
  joinIdentityGroup,
  leaveIdentityGroup,
  rejectIdentityGroupJoin,
  removeIdentityGroupMember,
  requestIdentityGroupJoin,
  setIdentityGroupMemberRole,
  subscribeIdentityGroupJoinRequests,
  subscribeIdentityGroupMembers,
  updateIdentityGroup,
  archiveIdentityGroup,
  type IdentityGroupRole,
} from "./identityGroups";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  VerificationStatus,
} from "../types/identity";

type GroupSummary = {
  id: string;
  name: string;
  isPublic: boolean;
  ownerIdentityId?: string;
  description?: string;
  subject?: string;
  status?: "active" | "archived" | string;
};

export type GroupMemberRole = IdentityGroupRole;

export type JoinRequestItem = {
  identityId: string;
  createdAt?: any;
  username?: string;
  displayName?: string;
  role?: LegacyAccountType;
  accountType?: AccountType;
  verificationStatus?: VerificationStatus;
  entitlements?: IdentityEntitlements;
  track?: string;
  mainSubject?: string;
  photoURL?: string;
};

export type GroupMemberItem = {
  identityId: string;
  role: GroupMemberRole;
  joinedAt?: any;
  username?: string;
  displayName?: string;
  profileRole?: LegacyAccountType;
  profileAccountType?: AccountType;
  profileVerificationStatus?: VerificationStatus;
  profileEntitlements?: IdentityEntitlements;
  track?: string;
  mainSubject?: string;
  photoURL?: string;
};

function mapAccount(identityId: string, account: any) {
  const legacyRole =
    account?.accountType === "student" || account?.accountType === "teacher"
      ? account.accountType
      : undefined;
  return {
    identityId,
    username: account?.username,
    displayName: account?.displayName,
    role: legacyRole,
    accountType: account?.accountType,
    verificationStatus: account?.verificationStatus,
    entitlements: account?.entitlements,
    track: account?.track,
    mainSubject: account?.mainSubject,
    photoURL: account?.photoURL,
  };
}

export async function syncPublicGroupJoin(params: {
  identityId: string;
  group: GroupSummary;
}) {
  await joinIdentityGroup(params.identityId, params.group.id);
}

export async function requestPrivateGroupJoin(params: {
  identityId: string;
  groupId: string;
}) {
  await requestIdentityGroupJoin(params.identityId, params.groupId);
}

export async function cancelPrivateGroupRequest(params: {
  identityId: string;
  groupId: string;
}) {
  await cancelIdentityGroupJoin(params.identityId, params.groupId);
}

export async function leaveGroupMembership(params: {
  identityId: string;
  group: GroupSummary;
}) {
  await leaveIdentityGroup(params.identityId, params.group.id);
}

export async function loadJoinRequests(groupId: string): Promise<JoinRequestItem[]> {
  return new Promise((resolve, reject) => {
    let unsubscribe = () => {};
    unsubscribe = subscribeIdentityGroupJoinRequests(
      groupId,
      async (identityIds) => {
        try {
          const accounts = await getPublicAccountsByIds(identityIds);
          const items = identityIds.map((identityId) => ({
            ...mapAccount(identityId, accounts[identityId]),
            createdAt: undefined,
          }));
          unsubscribe();
          resolve(items);
        } catch (error) {
          unsubscribe();
          reject(error);
        }
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });
}

export async function approveJoinRequest(params: {
  actorIdentityId: string;
  group: GroupSummary;
  targetIdentityId: string;
}) {
  await approveIdentityGroupJoin(params.actorIdentityId, params.group.id, params.targetIdentityId);
}

export async function rejectJoinRequest(params: {
  actorIdentityId: string;
  groupId: string;
  targetIdentityId: string;
}) {
  await rejectIdentityGroupJoin(params.actorIdentityId, params.groupId, params.targetIdentityId);
}

export async function loadGroupMembers(groupId: string): Promise<GroupMemberItem[]> {
  return new Promise((resolve, reject) => {
    let unsubscribe = () => {};
    unsubscribe = subscribeIdentityGroupMembers(
      groupId,
      async (members) => {
        try {
          const accounts = await getPublicAccountsByIds(members.map((item) => item.identityId));
          const items = members.map((member) => {
            const account = accounts[member.identityId];
            return {
              identityId: member.identityId,
              role: member.role,
              joinedAt: member.joinedAt,
              username: account?.username,
              displayName: account?.displayName,
              profileRole:
                account?.accountType === "student" || account?.accountType === "teacher"
                  ? account.accountType
                  : undefined,
              profileAccountType: account?.accountType,
              profileVerificationStatus: account?.verificationStatus,
              profileEntitlements: account?.entitlements,
              track: account?.track,
              mainSubject: account?.mainSubject,
              photoURL: account?.photoURL,
            } satisfies GroupMemberItem;
          });
          unsubscribe();
          resolve(items);
        } catch (error) {
          unsubscribe();
          reject(error);
        }
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });
}

export function listenGroupMembers(
  groupId: string,
  cb: (items: GroupMemberItem[]) => void,
  onErr?: (error: any) => void
) {
  let cancelled = false;
  return subscribeIdentityGroupMembers(
    groupId,
    async (members) => {
      try {
        const accounts = await getPublicAccountsByIds(members.map((item) => item.identityId));
        if (cancelled) return;
        cb(
          members.map((member) => {
            const account = accounts[member.identityId];
            return {
              identityId: member.identityId,
              role: member.role,
              joinedAt: member.joinedAt,
              username: account?.username,
              displayName: account?.displayName,
              profileRole:
                account?.accountType === "student" || account?.accountType === "teacher"
                  ? account.accountType
                  : undefined,
              profileAccountType: account?.accountType,
              profileVerificationStatus: account?.verificationStatus,
              profileEntitlements: account?.entitlements,
              track: account?.track,
              mainSubject: account?.mainSubject,
              photoURL: account?.photoURL,
            };
          })
        );
      } catch (error) {
        onErr?.(error);
      }
    },
    onErr
  );
}

export async function removeGroupMember(params: {
  group: GroupSummary;
  targetIdentityId: string;
  actorIdentityId: string;
}) {
  await removeIdentityGroupMember(params.actorIdentityId, params.group.id, params.targetIdentityId);
}

export async function toggleGroupVisibility(params: {
  group: GroupSummary;
  isPublic: boolean;
  actorIdentityId: string;
}) {
  await updateIdentityGroup({
    actorIdentityId: params.actorIdentityId,
    groupId: params.group.id,
    name: params.group.name,
    description: params.group.description ?? "",
    subject: params.group.subject ?? "general",
    isPublic: params.isPublic,
  });
}

export async function updateGroupDetails(params: {
  group: GroupSummary;
  name: string;
  description: string;
  subject: string;
  actorIdentityId: string;
}) {
  await updateIdentityGroup({
    actorIdentityId: params.actorIdentityId,
    groupId: params.group.id,
    name: params.name,
    description: params.description,
    subject: params.subject,
    isPublic: params.group.isPublic,
  });
}

export async function deleteGroupAndCleanup(params: GroupSummary & { actorIdentityId: string }) {
  await archiveGroup(params);
}

export async function archiveGroup(params: GroupSummary & { actorIdentityId: string }) {
  await archiveIdentityGroup(params.actorIdentityId, params.id);
}

export async function updateGroupMemberRole(params: {
  group: GroupSummary;
  actorIdentityId: string;
  targetIdentityId: string;
  role: "admin" | "member";
}) {
  await setIdentityGroupMemberRole(
    params.actorIdentityId,
    params.group.id,
    params.targetIdentityId,
    params.role
  );
}
